import { FC, lazy } from 'react';
import { KeyedMutator } from 'swr';

import { MediaMetadata } from './components/MediaMetadata';
import { ModalFullscreenPlayer } from './components/ModalFullscreenPlayer';
import { ProviderVariantModal } from './components/ProviderVariantModal';
const TransactionModal = lazy(() =>
  import('./components/TransactionModal').then(module => ({ default: module.TransactionModal }))
);

import { PinPanelModal } from '../../components/pin/PinPanel';
import { isOneOf } from '../../utils/types/typeUtils';

import { ScpHead } from './components/ScpHead';
import { getStackContent } from './hooks/scp.utils';
import { useContentStackEventHandlers } from './hooks/useContentStackEventHandlers';
import { SuperCoverPageEpisode, SuperCoverPageMovie } from './contentStack.types';
import { SectionRenderer } from './SectionRenderer';

import styles from './StackedEpisodeOrMoviePage.module.css';

export type StackedEpisodeOrMoviePageProps = {
  id: string;
  data: SuperCoverPageMovie | SuperCoverPageEpisode;
  dataMutator: KeyedMutator<SuperCoverPageMovie>;
};

export const StackedEpisodeOrMoviePage: FC<StackedEpisodeOrMoviePageProps> = ({ data, dataMutator }) => {
  const episodeOrMovie = getStackContent(data);
  const { sections } = episodeOrMovie;
  const showVariants = episodeOrMovie.defaultProviderVariant?.behavior === 'ShowVariants';

  const {
    assetId,
    isVariantModalOpen,
    isTransactionModalOpen,
    isPinModalOpen,
    selectedVariant,
    onClickPrimaryAction,
    onSelectProvider,
    onCloseVariantModal,
    onCloseTransactionlModal,
    onCloseParentalModal,
    onCloseModalPlayer,
    onAssetTransaction,
    onAccessGranted,
  } = useContentStackEventHandlers(episodeOrMovie.defaultProviderVariant, dataMutator);

  return (
    <section className={styles.movieCoverPage}>
      <ScpHead scpData={data} />
      {/* content */}
      <MediaMetadata media={episodeOrMovie} onClickPrimaryAction={onClickPrimaryAction} />
      <SectionRenderer sections={sections} onSelectProvider={onSelectProvider} />

      {/* modals */}
      {showVariants && (
        <ProviderVariantModal
          stack={episodeOrMovie}
          isOpen={isVariantModalOpen}
          onSelectProvider={onSelectProvider}
          onClose={onCloseVariantModal}
        />
      )}
      {(selectedVariant?.behavior === 'RentAsset' || selectedVariant?.behavior === 'PurchaseAsset') && (
        <TransactionModal
          isOpen={isTransactionModalOpen}
          onClose={onCloseTransactionlModal}
          transactionType={selectedVariant?.behavior === 'RentAsset' ? 'rental' : 'purchase'}
          transactionPrice={selectedVariant.price}
          onTransaction={onAssetTransaction}
          transactionLink={'rent' in selectedVariant._links ? selectedVariant._links.rent : selectedVariant._links.buy}
          voucherLink={selectedVariant._links.voucherInfo?.href}
          title={episodeOrMovie.metadata.title}
        />
      )}
      {selectedVariant && isPinModalOpen && (
        <PinPanelModal
          context={
            isOneOf(selectedVariant.behavior, ['RentAsset', 'PurchaseAsset'])
              ? 'transactionControl'
              : 'parentalControls'
          }
          assetId={selectedVariant.tracking.elementId}
          isOpen={isPinModalOpen}
          onClose={onCloseParentalModal}
          ageRestriction={episodeOrMovie.metadata.ageLimit}
          onAccessGranted={() => {
            onAccessGranted();
          }}
        />
      )}
      <ModalFullscreenPlayer assetId={assetId} onClose={onCloseModalPlayer} />
    </section>
  );
};
